const AUTOPLAY_FALLBACK_MS = 7000;
const SWIPE_THRESHOLD_PX = 48;

const initSliderSection = (section) => {
  const track = section.querySelector('[data-slider-track]');
  const slides = Array.from(section.querySelectorAll('[data-slider-slide]'));
  if (!track || slides.length === 0) return;

  const controls = section.querySelector('[data-slider-controls]');
  const dotsContainer = section.querySelector('[data-slider-dots]');
  const prevButton = section.querySelector('[data-slider-prev]');
  const nextButton = section.querySelector('[data-slider-next]');
  const status = section.querySelector('[data-slider-status]');
  const slideLabel = dotsContainer?.getAttribute('data-slide-label') || 'Slide';

  const setSlideState = (slide, isActive) => {
    slide.classList.toggle('is-active', isActive);
    if (isActive) {
      slide.removeAttribute('aria-hidden');
      slide.removeAttribute('inert');
    } else {
      slide.setAttribute('aria-hidden', 'true');
      slide.setAttribute('inert', '');
    }
  };

  if (slides.length < 2) {
    setSlideState(slides[0], true);
    section.style.setProperty('--slider-index', '0');
    return;
  }

  const dots = slides.map((_, index) => {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'slider-dot';
    dot.setAttribute('aria-label', `${slideLabel} ${index + 1}`);
    dotsContainer?.appendChild(dot);
    return dot;
  });

  controls?.removeAttribute('hidden');

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const autoplayAttr = Number.parseInt(section.getAttribute('data-slider-autoplay') || '', 10);
  const autoplayMs = Number.isFinite(autoplayAttr) && autoplayAttr > 0 ? autoplayAttr : AUTOPLAY_FALLBACK_MS;

  let current = 0;
  let autoplayTimer = null;
  let autoplayStopped = prefersReducedMotion;
  let isPaused = false;
  let hasAnnounced = false;

  const render = () => {
    section.style.setProperty('--slider-index', String(current));
    slides.forEach((slide, index) => setSlideState(slide, index === current));
    dots.forEach((dot, index) => {
      const isActive = index === current;
      dot.classList.toggle('is-active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    if (status) {
      // Keep the live region silent until the slide actually changes.
      status.textContent = hasAnnounced ? `${slideLabel} ${current + 1} / ${slides.length}` : '';
    }
  };

  const stopAutoplay = () => {
    autoplayStopped = true;
    if (autoplayTimer) {
      window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
    }
  };

  const scheduleAutoplay = () => {
    if (autoplayTimer) {
      window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
    }
    if (autoplayStopped || isPaused || document.hidden) return;

    autoplayTimer = window.setTimeout(() => {
      autoplayTimer = null;
      current = (current + 1) % slides.length;
      hasAnnounced = true;
      render();
      scheduleAutoplay();
    }, autoplayMs);
  };

  const goTo = (index) => {
    const next = ((index % slides.length) + slides.length) % slides.length;
    if (next !== current) {
      current = next;
      hasAnnounced = true;
      render();
    }
    // An explicit move means the visitor is driving; stop rotating on them.
    stopAutoplay();
  };

  prevButton?.addEventListener('click', () => goTo(current - 1));
  nextButton?.addEventListener('click', () => goTo(current + 1));
  dots.forEach((dot, index) => dot.addEventListener('click', () => goTo(index)));

  section.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(current - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(current + 1);
    }
  });

  const pause = () => {
    isPaused = true;
    if (autoplayTimer) {
      window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
    }
  };

  const resume = () => {
    isPaused = false;
    scheduleAutoplay();
  };

  section.addEventListener('pointerenter', pause);
  section.addEventListener('pointerleave', resume);
  section.addEventListener('focusin', pause);
  section.addEventListener('focusout', (event) => {
    if (!section.contains(event.relatedTarget)) resume();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      pause();
    } else {
      resume();
    }
  });

  // Touch / pointer dragging
  let pointerId = null;
  let startX = 0;
  let startY = 0;
  let deltaX = 0;
  let lastDragDistance = 0;
  let isDragging = false;

  const endDrag = () => {
    if (pointerId === null) return;
    pointerId = null;
    track.classList.remove('is-dragging');
    section.style.removeProperty('--slider-drag');

    lastDragDistance = isDragging ? deltaX : 0;

    if (isDragging && Math.abs(deltaX) > SWIPE_THRESHOLD_PX) {
      goTo(current + (deltaX < 0 ? 1 : -1));
    } else if (isDragging) {
      resume();
    }

    isDragging = false;
    deltaX = 0;
  };

  track.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (pointerId !== null) return;
    pointerId = event.pointerId;
    startX = event.clientX;
    startY = event.clientY;
    deltaX = 0;
    lastDragDistance = 0;
    isDragging = false;
  });

  track.addEventListener('pointermove', (event) => {
    if (event.pointerId !== pointerId) return;

    const dx = event.clientX - startX;
    const dy = event.clientY - startY;

    if (!isDragging) {
      // Let vertical scrolling win until the gesture is clearly horizontal.
      if (Math.abs(dx) < 10 || Math.abs(dx) <= Math.abs(dy)) return;
      isDragging = true;
      track.classList.add('is-dragging');
      pause();
    }

    deltaX = dx;
    section.style.setProperty('--slider-drag', `${dx}px`);
  });

  track.addEventListener('pointerup', endDrag);
  track.addEventListener('pointercancel', endDrag);

  track.addEventListener('click', (event) => {
    // Swallow the click that ends a swipe so the CTA is not triggered by accident.
    if (Math.abs(lastDragDistance) > 10) {
      event.preventDefault();
      lastDragDistance = 0;
    }
  });

  render();
  scheduleAutoplay();
};

export const initSliderSections = () => {
  const sections = document.querySelectorAll('[data-slider]');
  sections.forEach((section) => initSliderSection(section));
};
